import { useState, useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { FaExclamationCircle, FaClock } from 'react-icons/fa';
import orderService from "../services/orderService";
import { formatNumber } from "../utils/numberFormat";
import useIsMobile from '../hooks/useIsMobile';
import {
  formatPrezzoPane,
  mostraPrezziDestinazione,
  prezzoPaneVisibile,
} from '../utils/mostraPrezziDestinazione';

const getOreBloccoProd = (row) => {
  const raw =
    row?.dati_articolo?.orebloccoprod ??
    row?.data_forn_arti?.orebloccoprod ??
    row?.orebloccoprod;
  const n = Number(String(raw ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const formatOreBloccoLabel = (ore) => {
  if (!Number.isFinite(ore)) return String(ore);
  return Number.isInteger(ore) ? String(ore) : String(ore).replace(/\.0+$/, '');
};

const QuantityModal = ({
  showModal,
  setShowModal,
  selectedArticle,
  quantities = {}, // Fallback per evitare undefined
  setQuantities,
  handleSavePlanning,
  currentDay,
  darkMode,
  handleDeleteArticle,
  dayActivationFlags = {}, // AGGIUNTO: flags per giorni disabilitati
  destinazione = null, // AGGIUNTO: dati destinazione per fallback
  readOnlyWeekdayKeys = [], // es. ['martedi'] = colonne non editabili (domani in fascia oraria)
  getExtraReadOnlyKeysForArticle = null // es. oggi/domani per orebloccoprod (unite a readOnlyWeekdayKeys)
}) => {
  const daysOfWeek = ["lunedi", "martedi", "mercoledi", "giovedi", "venerdi", "sabato", "domenica"];

  const extraReadOnlyFromProd = useMemo(() => {
    if (typeof getExtraReadOnlyKeysForArticle !== 'function' || !selectedArticle) return [];
    const extra = getExtraReadOnlyKeysForArticle(selectedArticle);
    return Array.isArray(extra) ? extra : [];
  }, [getExtraReadOnlyKeysForArticle, selectedArticle]);

  const mergedReadOnlyWeekdayKeys = useMemo(() => {
    const base = Array.isArray(readOnlyWeekdayKeys) ? readOnlyWeekdayKeys : [];
    return [...new Set([...base, ...extraReadOnlyFromProd])];
  }, [readOnlyWeekdayKeys, extraReadOnlyFromProd]);

  const oreBloccoProdArticolo = selectedArticle ? getOreBloccoProd(selectedArticle) : 0;
  const isMobile = useIsMobile();

  // Stato per il modale di conferma eliminazione
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const eliminaPlanning = async (codice) => {
    try {
      await orderService.eliminaPlanning(codice);
    } catch (err) {
      console.error("Errore nell'eliminazione del Planning:", err);
    }
  };

  const handleQuantityChange = (day, value) => {
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [day]: value,
    }));
  };

  const handleSave = () => {
    // Controlla se tutte le quantità sono 0
    const allQuantitiesZero = daysOfWeek.every(day => Number(quantities[day] || 0) === 0);
    
    if (allQuantitiesZero) {
      // Se tutte le quantità sono 0, mostra il modale di conferma eliminazione
      setShowDeleteModal(true);
    } else {
      // Procedi con il salvataggio normale
      handleSavePlanning(); // Chiama la funzione di salvataggio
      setShowModal(false); // Chiude il modale
    }
  };


  // Aggiungi questa funzione dopo la dichiarazione delle props
const isDayDisabled = (day) => {
  // Prima controlla dayActivationFlags
  if (dayActivationFlags && Object.keys(dayActivationFlags).length > 0) {
    return dayActivationFlags[day] === '1';
  }
  
  // Fallback sui dati destinazione
  if (destinazione?.datiDestinazione) {
    const dayMapping = {
      'lunedi': 'lun_act',
      'martedi': 'mar_act',
      'mercoledi': 'mer_act',
      'giovedi': 'gio_act',
      'venerdi': 'ven_act',
      'sabato': 'sab_act',
      'domenica': 'dom_act'
    };
    const dayField = dayMapping[day];
    if (dayField) {
      return destinazione.datiDestinazione[dayField] === '1';
    }
  }
  
  return false; // Default: giorno attivo
};


  // Mostra il modale di conferma eliminazione
  const handleAskDelete = () => {
    setShowDeleteModal(true);
  };

  // Conferma eliminazione
  const handleConfirmDelete = async () => {
    await eliminaPlanning(selectedArticle.codice);
    if (handleDeleteArticle) {
      handleDeleteArticle(selectedArticle.codice);
    }
    setShowDeleteModal(false);
    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false); // Chiude il modale
  };

  return (
    <>
      <Modal 
        show={showModal} 
        onHide={handleCancel}
        centered
        fullscreen={isMobile || undefined}
      >
        <Modal.Header closeButton className={darkMode ? 'bg-dark text-light' : ''}>
          <Modal.Title>Modifica articolo</Modal.Title>
        </Modal.Header>
        <Modal.Body className={darkMode ? 'bg-dark text-light' : ''}>
          {selectedArticle && (
            <>
              <div className="mb-3">
                <p><strong>Codice prodotto:</strong> {selectedArticle.codice}</p>
                <p><strong>Descrizione:</strong> {selectedArticle.descriz}</p>
                <p><strong>Unità di misura:</strong> {selectedArticle.unitaMisura}</p>
                {mostraPrezziDestinazione(destinazione) && prezzoPaneVisibile(selectedArticle.prezzo) && (
                  <p>
                    <strong>Prezzo:</strong>{' '}
                    {formatPrezzoPane(
                      selectedArticle.prezzo,
                      selectedArticle.unitaMisura || selectedArticle.unita_m || selectedArticle.unita
                    )}
                  </p>
                )}
                {selectedArticle.immagine && (
                  <div className="mb-3 text-center">
                    <img 
                      src={selectedArticle.immagine} 
                      alt={selectedArticle.descriz}
                      style={{ maxHeight: '100px', maxWidth: '150px' }} 
                      className="img-thumbnail"
                    />
                  </div>
                )}
                {selectedArticle.schedaTecnica && (
                  <p><strong>Scheda tecnica:</strong> {selectedArticle.schedaTecnica}</p>
                )}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Quantità per i giorni della settimana:</Form.Label>
                {oreBloccoProdArticolo > 0 && (
                  <Alert
                    variant={darkMode ? 'dark' : 'light'}
                    className="py-2 px-3 small mb-3 border border-danger border-opacity-50"
                  >
                    <div className="d-flex align-items-start gap-2">
                      <FaClock className="text-danger mt-1 flex-shrink-0" aria-hidden />
                      <span>
                        Il prodotto prevede la modifica dell&apos;ordine{' '}
                        <strong className="text-danger">{formatOreBloccoLabel(oreBloccoProdArticolo)}</strong>
                        {' '}ore prima del lancio di produzione.
                      </span>
                    </div>
                  </Alert>
                )}
                <Row>
                  {daysOfWeek.map((day) => {
                    // Calcolo dei parametri per la select
                    const isKg = (selectedArticle.unitaMisura?.toLowerCase() === "kg");
                    let range = Number(selectedArticle.qtaRange) || 0;
                   
                    if (range === 0) range = isKg ? 0.1 : 1;
                      const min = Number(selectedArticle.qtaMinima) || range;
                      const max = Number(selectedArticle.qtaMax) || (isKg ? 100 : 1000); // valore massimo arbitrario
                    
                      // Genera i valori disponibili
                    let values = [];
                    for (let v = min; v <= max; v = +(v + range).toFixed(3)) {
                      values.push(isKg ? v.toFixed(3) : v.toString());
                    }

                    // Forza il valore della select a stringa con punto decimale
                    let quantitaValue = quantities?.[day];
                    if (typeof quantitaValue === "string") {
                      quantitaValue = quantitaValue.replace(",", "."); // sostituisci eventuale virgola con punto
                    }
                    quantitaValue = Number(quantitaValue);
                    
                    const selectValue = quantitaValue === 0
                      ? "0"
                      : isKg
                        ? quantitaValue.toFixed(3)
                        : quantitaValue.toString();

                    const optionExists = ["0", ...values].includes(selectValue);

                    const lockedByOreProd = extraReadOnlyFromProd.includes(day);
                    const lockedByFascia =
                      Array.isArray(readOnlyWeekdayKeys) && readOnlyWeekdayKeys.includes(day);
                    const bloccoProdGiorno = lockedByOreProd || lockedByFascia;
                    const bloccoProdTitle = lockedByOreProd
                      ? "Troppo tardi per modificare l'ordine per questo giorno (tempo minimo di produzione)"
                      : "In questa fascia oraria non è consentito modificare le quantità per domani.";
                    const bloccoProdAria = lockedByOreProd
                      ? "Troppo tardi per modificare l'ordine per questo giorno"
                      : "Modifiche non consentite per domani in fascia oraria";

                    return (
                      <Col key={day} xs={6} md={4} className="mb-2">
                        <Form.Text
                          className={`d-flex align-items-center gap-1 flex-wrap ${
                            day === currentDay ? 'text-muted fw-bold' : 'text-muted'
                          }`}
                        >
                          <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                          {bloccoProdGiorno && (
                            <FaExclamationCircle
                              className="text-danger flex-shrink-0"
                              style={{ fontSize: '1rem' }}
                              title={bloccoProdTitle}
                              aria-label={bloccoProdAria}
                            />
                          )}
                        </Form.Text>

                        <Form.Select
                          value={selectValue}
                          onChange={e => handleQuantityChange(day, Number(e.target.value))}
                          className={`
                            ${day === currentDay ? "bg-primary text-light" : ""}
                            ${isDayDisabled(day) ? "border-danger bg-danger-subtle" : ""}
                          `.trim()}
                          disabled={
                            isDayDisabled(day) ||
                            mergedReadOnlyWeekdayKeys.includes(day)
                          }
                        >
                          <option value={0}>0</option>
                          {values.map(val => {
                             let conversionText = "";
                             const peso = Number(selectedArticle.peso) || 0;
                             const numVal = Number(val);
 
                             if (peso > 0 && numVal > 0) {
                               if (isKg) {
                                 const pezzi = (numVal / peso).toFixed(0);
                                 conversionText = ` (≈ ${formatNumber(pezzi)} pz)`;
                               } else {
                                 const kg = (numVal * peso).toFixed(3);
                                 conversionText = ` (≈ ${formatNumber(kg)} kg)`;
                               }
                             }

                            return val !== "0" && (
                              <option key={val} value={val}>
                                {formatNumber(val, isKg ? 3 : 0)}{conversionText}
                              </option>
                            )
                          }
                          )}
                          {!optionExists && selectValue !== "0" && (
                            <option value={selectValue}>{formatNumber(selectValue, isKg ? 3 : 0)} (fuori range)</option>
                          )}
                        </Form.Select>
                        <Form.Text className={`
                          ${day === currentDay ? "text-muted fw-bold" : "text-muted"}
                          ${isDayDisabled(day) ? "text-danger fw-bold" : ""}
                        `.trim()}>
                          {(() => {
                            const qty = Number(quantities?.[day] || 0);
                            const peso = Number(selectedArticle.peso) || 0;
                            const isKgUnit = selectedArticle.unitaMisura?.toLowerCase() === "kg";
                            
                            if (qty === 0) return "";
                            
                            if (isKgUnit && peso > 0) {
                              // Converti da kg a pezzi
                              const pezzi = (qty / peso).toFixed(0);
                              return `≈ ${formatNumber(pezzi)} pz`;
                            } else if (!isKgUnit && peso > 0) {
                              // Converti da pezzi a kg
                              const kg = (qty * peso).toFixed(3);
                              return `≈ ${formatNumber(kg)} kg`;
                            }
                            
                            return "";
                          })()}
                          {isDayDisabled(day) && (
                            <> <i className="fas fa-ban ms-1" title="Giorno disabilitato"></i></>
                          )}
                        </Form.Text>
                      </Col>
                    );
                  })}
                </Row>
              </Form.Group>

              <div className="mt-3">
                <p className="text-muted">
                  {(() => {
                    const unita = selectedArticle.unitaMisura || selectedArticle.unita;
                    const isKgUnit = unita && unita.toLowerCase() === "kg";
                    console.log("QuantityModal - Unità:", unita, "isKg:", isKgUnit); // Debug
                    
                    return isKgUnit ? 
                      "Le quantità di questo articolo sono a peso. Il calcolo peso/pezzi è solamente indicativo." :
                      "Le quantità di questo articolo sono a Pz. Il calcolo del peso è solamente indicativo.";
                  })()}
                </p>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className={darkMode ? 'bg-dark text-light' : ''}>
          <Button 
            variant="secondary" 
            onClick={handleCancel}
          >
            Annulla
          </Button>
          <Button 
            variant="success" 
            onClick={handleSave}
          >
            <i className="fas fa-save me-1"></i> Salva
          </Button>
          <Button 
            variant="danger" 
            onClick={handleAskDelete}
          >
            <i className="fas fa-trash me-1"></i> Elimina
          </Button>
        </Modal.Footer>
      </Modal>




      {/* Modale di conferma eliminazione */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Conferma eliminazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Vuoi procedere con l'eliminazione di questo articolo da tutti i giorni della settimana?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          { /*
          <Button variant="success" onClick={() => {
            // Salva con quantità 0 senza eliminare
            handleSavePlanning();
            setShowDeleteModal(false);
            setShowModal(false);
          }}>
            Salva con quantità 0
          </Button>
          */ }
          <Button variant="danger" onClick={handleConfirmDelete}>
            Elimina articolo
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuantityModal;